import { useFormikFieldState } from "@components/forms/FormikField/FormikField";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { ChoiceFieldLabel, ChoiceFieldShell } from "./ChoiceFieldShell";
import type { FieldPath } from "./fieldPath.types";
import type { KitChoiceFieldProps } from "./kitFieldProps.types";

/** Paths of `Values` that hold a plain `boolean`. */
export type CheckboxFieldPath<Values> = FieldPath<Values, boolean, boolean>;

export type CheckboxFieldProps<Name extends string> = KitChoiceFieldProps<
    Name,
    HTMLElement,
    boolean
>;

/**
 * Checkbox bound to a typed `boolean` Formik path. The label sits beside the
 * box (`labelHidden` keeps it for screen readers only; `aria-labelledby`
 * replaces it); the error shows below after blur or submit. Values with a
 * third state (`boolean | null`) stay on `FormikField`.
 */
export const CheckboxField = <
    Values,
    Name extends CheckboxFieldPath<Values> = CheckboxFieldPath<Values>,
>({
    name,
    label,
    labelHidden,
    "aria-labelledby": ariaLabelledby,
    optional,
    description,
    className,
    showErrorMessage,
    ref,
    disabled,
    readOnly,
    required,
    onValueChange,
}: CheckboxFieldProps<Name>) => {
    const { field, helpers, invalid, error, rootRef } =
        useFormikFieldState<boolean>({
            name,
            showErrorMessage,
        });

    return (
        <ChoiceFieldShell
            ref={rootRef}
            invalid={invalid}
            error={error}
            description={description}
            className={className}
        >
            <Checkbox
                ref={ref}
                name={name}
                checked={field.value}
                onCheckedChange={(checked) => {
                    void helpers.setValue(checked);
                    onValueChange?.(checked);
                }}
                onBlur={() => void helpers.setTouched(true)}
                invalid={invalid}
                disabled={disabled}
                readOnly={readOnly}
                required={required}
                label={
                    ariaLabelledby === undefined ? (
                        <ChoiceFieldLabel label={label} optional={optional} />
                    ) : undefined
                }
                labelHidden={labelHidden}
                aria-labelledby={ariaLabelledby}
            />
        </ChoiceFieldShell>
    );
};
